import { notification } from 'antd';
import React, { useEffect } from 'react';

interface AntdNotificationProps {
  type?: 'success' | 'info' | 'warning' | 'error';
  message?: string;
  description?: string;
  duration?: number; // default: 4.5 seconds
  visible?: boolean;
  onHide?: () => void;
}

interface LegacyNotificationProps {
  content?: string;
  color?: 'green' | 'red' | 'orange' | 'blue' | 'info' | 'warning' | 'success' | 'error';
  active?: boolean;
  pointing?: 'left' | 'right' | 'top' | 'bottom';
  size?: 'small' | 'default' | 'large';
  autoHide?: boolean;
  autoHideSeconds?: number;
  visible?: boolean;
  onHide?: () => void;
}

type NotificationProps = AntdNotificationProps & LegacyNotificationProps & {
  dedupeKey?: string;
};


const mapColorToType = (
  color?: LegacyNotificationProps['color']
): NonNullable<AntdNotificationProps['type']> => {
  switch (color) {
    case 'green':
    case 'success':
      return 'success';
    case 'red':
    case 'error':
      return 'error';
    case 'orange':
    case 'warning':
      return 'warning';
    case 'blue':
    case 'info':
    default:
      return 'info';
  }
};

const normalizeDurationSeconds = (
  explicitDuration?: number,
  autoHideSeconds?: number,
  autoHide?: boolean
): number | undefined => {
  if (typeof explicitDuration === 'number') return explicitDuration;
  if (autoHideSeconds !== undefined) {
    return autoHideSeconds > 1000 ? autoHideSeconds / 1000 : autoHideSeconds;
  }
  if (autoHide === false) return undefined;
  return undefined;
};

const Notification: React.FC<NotificationProps> = (props) => {
  const {
    type,
    message,
    description,
    duration,
    visible,
    onHide,
    content,
    color,
    active,
    autoHide,
    autoHideSeconds,
    dedupeKey
  } = props;

  const onHideRef = React.useRef<(() => void) | undefined>(onHide);
  React.useEffect(() => {
    onHideRef.current = onHide;
  }, [onHide]);

  const prevVisibleRef = React.useRef(false);
  const lastSignatureRef = React.useRef<string>("");

  useEffect(() => {
    const isVisible = visible ?? active ?? false;
    if (!isVisible) {
      prevVisibleRef.current = false;
      return;
    }

    const antdType = (type ?? mapColorToType(color)) as 'success' | 'info' | 'warning' | 'error';
    const msg = message ?? content ?? '';
    const dur = normalizeDurationSeconds(duration, autoHideSeconds, autoHide);

    const key = dedupeKey ?? `${antdType}:${msg}`;

    if (!isVisible || !msg) {
      prevVisibleRef.current = false;
      return;
    }

    const signature = JSON.stringify({
      antdType,
      msg,
      description: description ?? "",
      dur: dur ?? null,
    });

    const becameVisible = !prevVisibleRef.current && isVisible;
    const changed = lastSignatureRef.current !== signature;

    if (becameVisible || changed) {
      lastSignatureRef.current = signature;

      notification.open({
        key,
        type: antdType,
        message: msg,
        description,
        duration: dur,
        onClose: () => onHideRef.current?.(),
      } as any);
    }

    prevVisibleRef.current = isVisible;
  }, [
    visible,
    active,
    type,
    color,
    message,
    content,
    description,
    duration,
    autoHide,
    autoHideSeconds,
    dedupeKey,
  ]);

  return null;
};

export default Notification;